import { useQuery } from '@tanstack/react-query';

export interface TopQuery {
  queryid: string;
  query_fingerprint: string;
  calls: number;
  total_exec_time_ms: number;
  mean_exec_time_ms: number;
  max_exec_time_ms: number;
  stddev_exec_time_ms: number;
  rows: number;
}

export interface TableStat {
  table_name: string;
  seq_scan: number;
  idx_scan: number;
  seq_tup_read: number;
  n_live_tup: number;
  n_dead_tup: number;
  seq_scan_ratio: number;
  dead_row_ratio: number;
}

export interface SlowLogRow {
  timestamp: string;
  service?: string;
  query_fingerprint?: string;
  duration_ms?: number;
  route_pattern?: string;
  worker_name?: string;
  job_id?: string;
}

export interface DbPerfConfig {
  db_perf_slow_query_threshold_ms?: string;
  db_perf_seq_scan_ratio_floor?: string;
  db_perf_seq_scan_min_rows?: string;
  db_perf_query_alert_threshold_ms?: string;
}

const REFETCH_MS = 30_000;

export type TopQuerySort = 'total_exec_time' | 'mean_exec_time' | 'calls';

export function useTopQueries(sort: TopQuerySort = 'total_exec_time') {
  return useQuery<TopQuery[]>({
    queryKey: ['db-perf', 'top-queries', sort],
    queryFn: async () => {
      const r = await fetch(`/api/admin/db-perf/top-queries?limit=20&sort=${sort}`);
      if (!r.ok) throw new Error(`top-queries: ${r.status}`);
      return r.json() as Promise<TopQuery[]>;
    },
    refetchInterval: REFETCH_MS,
  });
}

export function useTableStats() {
  return useQuery<TableStat[]>({
    queryKey: ['db-perf', 'table-stats'],
    queryFn: async () => {
      const r = await fetch('/api/admin/db-perf/table-stats');
      if (!r.ok) throw new Error(`table-stats: ${r.status}`);
      return r.json() as Promise<TableStat[]>;
    },
    refetchInterval: REFETCH_MS,
  });
}

export function useSlowLog(limit = 200, since = '15m') {
  return useQuery<SlowLogRow[]>({
    queryKey: ['db-perf', 'slow-log', limit, since],
    queryFn: async () => {
      const r = await fetch(`/api/admin/db-perf/slow-log?since=${since}&limit=${limit}`);
      if (!r.ok) throw new Error(`slow-log: ${r.status}`);
      return r.json() as Promise<SlowLogRow[]>;
    },
    refetchInterval: REFETCH_MS,
  });
}

export function useDbPerfConfig() {
  return useQuery<DbPerfConfig>({
    queryKey: ['db-perf', 'config'],
    queryFn: async () => {
      const r = await fetch('/api/admin/db-perf/config');
      if (!r.ok) throw new Error(`config: ${r.status}`);
      return r.json() as Promise<DbPerfConfig>;
    },
  });
}
